import React, { useCallback, useEffect, useRef, useState } from 'react';
import socket from '../socket';
import { buildApiUrl, fetchJson, resolveMediaUrl } from '../utils/api';
import Button from './ui/Button';
import './css/OptionsView.css';

// ─── Konstanten ──────────────────────────────────────────────────────────────

const PHASES = [
    { key: 'inter',            label: 'Zwischenphase',      desc: 'Läuft zwischen zwei Fragen.' },
    { key: 'question',         label: 'Fragenphase',        desc: 'Startet wenn eine Frage angezeigt wird.' },
    { key: 'reveal',           label: 'Aufdeckphase',       desc: 'Startet wenn ein Team antwortet.' },
    { key: 'finale_decoder',   label: 'Finale (Decoder)',   desc: 'Läuft während des Decoder-Finales.' },
    { key: 'finale_quizpoker', label: 'Finale (Quizpoker)', desc: 'Läuft während des Quizpoker-Finales.' },
];

const EFFECTS = [
    { key: 'correct', label: 'Richtige Antwort', desc: 'Einmal abgespielt bei korrekter Antwort.' },
    { key: 'wrong',   label: 'Falsche Antwort',  desc: 'Einmal abgespielt bei falscher Antwort.' },
    { key: 'select',  label: 'Auswahl',           desc: 'Einmal abgespielt bei Moderator-Aktionen.' },
];

const DEFAULT_MUSIC_CONFIG = {
    inter:             { url: '/sounds/inter_loop.mp3',    volume: 0.05, fadeDuration: 1.0, loop: true },
    question:          { url: '/sounds/question_loop.mp3', volume: 0.40, fadeDuration: 1.0, loop: true },
    reveal:            { url: '/sounds/reveal_loop.mp3',   volume: 0.40, fadeDuration: 1.0, loop: true },
    finale_decoder:    { url: '/sounds/finale_loop.mp3',   volume: 0.15, fadeDuration: 1.0, loop: true },
    finale_quizpoker:  { url: '/sounds/finale_loop.mp3',   volume: 0.15, fadeDuration: 1.0, loop: true },
};
const DEFAULT_SOUND_EFFECTS_CONFIG = {
    correct: { url: '/sounds/correct.mp3', volume: 0.5 },
    wrong:   { url: '/sounds/wrong.mp3',   volume: 0.5 },
    select:  { url: '/sounds/select.mp3',  volume: 0.5 },
};
const DEFAULT_SCORE_CONFIG = { leicht: 2, mittel: 3, schwer: 4 };
const DEFAULT_VIEWER_INFO_TEXT = 'Um mitzuspielen, ruft auf eurem eigenen Handy oder Laptop diese Adresse auf und meldet euch dort mit eurem Namen an.';

// ─── Hilfkomponenten ─────────────────────────────────────────────────────────

function LoopToggle({ active, onChange }) {
    return (
        <button
            type="button"
            className={`loop-toggle ${active ? 'loop-toggle--on' : 'loop-toggle--off'}`}
            onClick={() => onChange(!active)}
        >
            ↺ {active ? 'Loop an' : 'Loop aus'}
        </button>
    );
}

// Aufklappbare Haupt-Sektion mit CSS-Transition (kein Layout-Sprung)
function TopSection({ title, defaultOpen = true, children }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="top-section">
            <button type="button" className="top-section-toggle" onClick={() => setOpen((v) => !v)}>
                <span className="top-section-icon">{open ? '▾' : '▸'}</span>
                {title}
            </button>
            <div className={`top-section-body-outer${open ? ' top-section-body-outer--open' : ''}`}>
                <div className="top-section-body">
                    {children}
                </div>
            </div>
        </div>
    );
}

// Aufklappbare Unter-Sektion innerhalb Audio
function SubSection({ title, children }) {
    return (
        <div className="sub-section">
            <h4 className="sub-section-title">{title}</h4>
            {children}
        </div>
    );
}

// Aufklappbare Card – ▶/⏹ zeigt und steuert manualAudio
function CollapsibleCard({ label, desc, isPlaying, onPlayToggle, children }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="phase-card">
            <div className="phase-card-header" onClick={() => setExpanded((v) => !v)}>
                <span className="phase-toggle-icon">{expanded ? '▾' : '▸'}</span>
                <span className="phase-label">{label}</span>
                <span className="phase-desc-inline">{desc}</span>
                <button
                    type="button"
                    className={`preview-btn${isPlaying ? ' preview-btn--active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); onPlayToggle?.(); }}
                    title={isPlaying ? 'Wiedergabe stoppen' : 'In Show abspielen (Fallback)'}
                >
                    {isPlaying ? '⏹' : '▶'}
                </button>
            </div>
            {expanded && <div className="phase-card-body">{children}</div>}
        </div>
    );
}

// ─── Phase-Karte ─────────────────────────────────────────────────────────────

function PhaseCard({ phaseKey, label, desc, cfg, allTracks, onUpdate, onUpload, onPlayToggle, isPlaying, isAutoPlaying }) {
    const [localVolume, setLocalVolume] = useState(cfg.volume);
    const [localFade, setLocalFade] = useState(cfg.fadeDuration ?? 1.0);

    useEffect(() => { setLocalVolume(cfg.volume); }, [cfg.volume]);
    useEffect(() => { setLocalFade(cfg.fadeDuration ?? 1.0); }, [cfg.fadeDuration]);

    const loopEnabled = cfg.loop !== false;

    return (
        <CollapsibleCard
            label={label} desc={desc}
            isPlaying={isPlaying || isAutoPlaying}
            onPlayToggle={() => onPlayToggle(cfg.url, cfg.volume, cfg.loop !== false)}
        >
            <div className="phase-row">
                <label className="phase-field-label">Datei</label>
                <div className="phase-file-row">
                    <select value={cfg.url} onChange={(e) => onUpdate(phaseKey, { url: e.target.value })} className="phase-select">
                        {allTracks.map((t) => <option key={t.url} value={t.url}>{t.originalName}</option>)}
                    </select>
                    <Button size="sm" variant="secondary" onClick={() => onUpload(phaseKey)}>Hochladen</Button>
                </div>
            </div>
            <div className="phase-row">
                <label className="phase-field-label">Lautstärke: {Math.round(localVolume * 100)} %</label>
                <input type="range" min="0" max="1" step="0.01" value={localVolume}
                    onChange={(e) => setLocalVolume(parseFloat(e.target.value))}
                    onMouseUp={() => onUpdate(phaseKey, { volume: localVolume })}
                    onTouchEnd={() => onUpdate(phaseKey, { volume: localVolume })}
                    className="phase-slider" />
            </div>
            <div className="phase-row phase-row--inline">
                <span className="phase-field-label">Loop</span>
                <LoopToggle active={loopEnabled} onChange={(v) => onUpdate(phaseKey, { loop: v })} />
            </div>
            {loopEnabled && (
                <div className="phase-row">
                    <label className="phase-field-label">Überblendzeit: {localFade.toFixed(1)} s</label>
                    <input type="range" min="0.2" max="5" step="0.1" value={localFade}
                        onChange={(e) => setLocalFade(parseFloat(e.target.value))}
                        onMouseUp={() => onUpdate(phaseKey, { fadeDuration: localFade })}
                        onTouchEnd={() => onUpdate(phaseKey, { fadeDuration: localFade })}
                        className="phase-slider" />
                </div>
            )}
        </CollapsibleCard>
    );
}

// ─── Effekt-Karte ─────────────────────────────────────────────────────────────

function EffectCard({ effectKey, label, desc, cfg, allTracks, onUpdate, onUpload, onPlayToggle, isPlaying }) {
    const [localVolume, setLocalVolume] = useState(cfg.volume);
    useEffect(() => { setLocalVolume(cfg.volume); }, [cfg.volume]);

    return (
        <CollapsibleCard
            label={label} desc={desc}
            isPlaying={isPlaying}
            onPlayToggle={() => onPlayToggle(cfg.url, cfg.volume, false)}
        >
            <div className="phase-row">
                <label className="phase-field-label">Datei</label>
                <div className="phase-file-row">
                    <select value={cfg.url} onChange={(e) => onUpdate(effectKey, { url: e.target.value })} className="phase-select">
                        {allTracks.map((t) => <option key={t.url} value={t.url}>{t.originalName}</option>)}
                    </select>
                    <Button size="sm" variant="secondary" onClick={() => onUpload(effectKey)}>Hochladen</Button>
                </div>
            </div>
            <div className="phase-row">
                <label className="phase-field-label">Lautstärke: {Math.round(localVolume * 100)} %</label>
                <input type="range" min="0" max="1" step="0.01" value={localVolume}
                    onChange={(e) => setLocalVolume(parseFloat(e.target.value))}
                    onMouseUp={() => onUpdate(effectKey, { volume: localVolume })}
                    onTouchEnd={() => onUpdate(effectKey, { volume: localVolume })}
                    className="phase-slider" />
            </div>
        </CollapsibleCard>
    );
}

// ─── Presets ─────────────────────────────────────────────────────────────────

function PresetsSection() {
    const [presets, setPresets] = useState([]);
    const [activePresetId, setActivePresetId] = useState(null);
    const [newName, setNewName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        socket.emit('presets:list', ({ presets: list, activePresetId: aid } = {}) => {
            if (Array.isArray(list)) setPresets(list);
            setActivePresetId(aid ?? null);
        });
        const handleChanged = ({ presets: list, activePresetId: aid } = {}) => {
            if (Array.isArray(list)) setPresets(list);
            setActivePresetId(aid ?? null);
        };
        socket.on('presets:changed', handleChanged);
        return () => socket.off('presets:changed', handleChanged);
    }, []);

    const handleSave = () => {
        if (!newName.trim()) return;
        setSaving(true);
        setError('');
        socket.emit('presets:save', { name: newName.trim() }, (res) => {
            setSaving(false);
            if (res?.ok) setNewName('');
            else setError(res?.error || 'Fehler beim Speichern');
        });
    };

    const handleApply = (id) => socket.emit('presets:apply', { id });
    const handleDelete = (id) => socket.emit('presets:delete', { id });

    const activePreset = presets.find((p) => p.id === activePresetId);

    return (
        <SubSection title="Presets">
            {activePreset && (
                <div className="preset-active-bar">
                    <span>
                        Aktiv: <strong>{activePreset.name}</strong>
                        <span className="preset-autosave-hint"> – Änderungen werden automatisch gespeichert</span>
                    </span>
                </div>
            )}

            <div className="preset-save-row">
                <input
                    type="text"
                    className="preset-name-input"
                    placeholder="Neues Preset benennen…"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                />
                <Button variant="secondary" disabled={saving || !newName.trim()} onClick={handleSave}>
                    {saving ? 'Speichert…' : 'Speichern'}
                </Button>
                {error && <span className="options-error">{error}</span>}
            </div>

            {presets.length > 0 && (
                <ul className="preset-list">
                    {presets.map((p) => (
                        <li key={p.id} className={`preset-item ${p.id === activePresetId ? 'preset-item--active' : ''}`}>
                            <span className="preset-name">
                                {p.name}
                                {p.id === activePresetId && <span className="preset-active-dot" title="Aktives Preset" />}
                            </span>
                            <div className="preset-actions">
                                <Button size="sm" variant="primary" onClick={() => handleApply(p.id)}>Anwenden</Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)}>✕</Button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </SubSection>
    );
}

// ─── OptionsView Hauptkomponente ─────────────────────────────────────────────

function OptionsView() {
    const [musicConfig, setMusicConfig] = useState(DEFAULT_MUSIC_CONFIG);
    const [soundEffectsConfig, setSoundEffectsConfig] = useState(DEFAULT_SOUND_EFFECTS_CONFIG);
    const [scoreConfig, setScoreConfig] = useState(DEFAULT_SCORE_CONFIG);
    const [viewerInfoText, setViewerInfoText] = useState(DEFAULT_VIEWER_INFO_TEXT);
    const [viewerInfoLink, setViewerInfoLink] = useState('');
    const [library, setLibrary] = useState([]);
    const [bgMusic, setBgMusic] = useState({ url: null, volume: 0.5, playing: false, fadeDuration: 1.0, loop: true });
    const [manualAudio, setManualAudio] = useState({ url: null, volume: 0.5, playing: false, loop: false });
    const [soundStatus, setSoundStatus] = useState({ questionPlaying: false, revealPlaying: false, autoPhase: null });
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadTargetPhase, setUploadTargetPhase] = useState(null);
    const fileInputRef = useRef(null);

    const allTracks = library;

    const loadLibrary = useCallback(async () => {
        try {
            const data = await fetchJson('/api/music/library');
            setLibrary(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('[OptionsView] Bibliothek laden fehlgeschlagen:', e);
        }
    }, []);

    useEffect(() => {
        loadLibrary();
        socket.emit('musicConfig:getConfig', (cfg) => { if (cfg) setMusicConfig((p) => ({ ...p, ...cfg })); });
        socket.emit('soundEffects:getConfig', (cfg) => { if (cfg) setSoundEffectsConfig((p) => ({ ...p, ...cfg })); });
        socket.emit('bgMusic:getState', (state) => { if (state) setBgMusic((p) => ({ ...p, ...state })); });
        socket.emit('scoreConfig:get', (cfg) => { if (cfg) setScoreConfig((p) => ({ ...p, ...cfg })); });
        socket.emit('manualAudio:getState', (state) => { if (state) setManualAudio((p) => ({ ...p, ...state })); });
        socket.emit('viewerInfo:getConfig', (cfg) => {
            if (cfg?.text) setViewerInfoText(cfg.text);
            setViewerInfoLink(cfg?.link || '');
        });

        const onMusicConfig = (cfg) => setMusicConfig((p) => ({ ...p, ...cfg }));
        const onSoundEffects = (cfg) => setSoundEffectsConfig((p) => ({ ...p, ...cfg }));
        const onBgMusic = (state) => setBgMusic((p) => ({ ...p, ...state }));
        const onScoreConfig = (cfg) => setScoreConfig((p) => ({ ...p, ...cfg }));
        const onManualAudio = (state) => setManualAudio((p) => ({ ...p, ...state }));
        const onViewerInfo = (cfg) => {
            if (cfg?.text) setViewerInfoText(cfg.text);
            setViewerInfoLink(cfg?.link || '');
        };
        const onQuizState = (state) => {
            if (state?.manualAudio) setManualAudio((p) => ({ ...p, ...state.manualAudio }));
            if (state?.bgMusic) setBgMusic((p) => ({ ...p, ...state.bgMusic }));
            if (state?.soundStatus) setSoundStatus((p) => ({ ...p, ...state.soundStatus }));
        };

        socket.on('musicConfig:update', onMusicConfig);
        socket.on('soundEffects:update', onSoundEffects);
        socket.on('bgMusic:state', onBgMusic);
        socket.on('scoreConfig:update', onScoreConfig);
        socket.on('manualAudio:state', onManualAudio);
        socket.on('quizState', onQuizState);
        socket.on('viewerInfo:update', onViewerInfo);
        return () => {
            socket.off('musicConfig:update', onMusicConfig);
            socket.off('soundEffects:update', onSoundEffects);
            socket.off('bgMusic:state', onBgMusic);
            socket.off('scoreConfig:update', onScoreConfig);
            socket.off('manualAudio:state', onManualAudio);
            socket.off('quizState', onQuizState);
            socket.off('viewerInfo:update', onViewerInfo);
        };
    }, [loadLibrary]);

    const handlePhaseUpdate = useCallback((phase, changes) => socket.emit('musicConfig:setPhase', { phase, ...changes }), []);
    const handleEffectUpdate = useCallback((sound, changes) => socket.emit('soundEffects:setSound', { sound, ...changes }), []);

    // ▶ / ⏹: optimistisches lokales Update + Socket-Broadcast
    const handlePlayToggle = useCallback((url, volume, loop) => {
        const alreadyPlaying = manualAudio.playing && manualAudio.url === url;
        const newPlaying = !alreadyPlaying;
        // Sofortiges lokales Update damit der Button reagiert
        setManualAudio((p) => ({ ...p, url, volume, loop: !!loop, playing: newPlaying }));
        socket.emit('manualAudio:set', { url, volume, loop: !!loop, playing: newPlaying }, (res) => {
            // Bei Ablehnung durch Server zurücksetzen
            if (res && !res.ok) setManualAudio((p) => ({ ...p, playing: false }));
        });
    }, [manualAudio]);

    const handleOpenUpload = useCallback((target = null) => {
        setUploadTargetPhase(target);
        setUploadError('');
        fileInputRef.current?.click();
    }, []);

    const handleFileUpload = async (file) => {
        if (!file) return;
        if (!file.type.startsWith('audio/')) {
            setUploadError('Bitte eine Audiodatei auswählen (MP3, WAV, OGG, …)');
            return;
        }
        setUploading(true);
        setUploadError('');
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await fetch(buildApiUrl('/api/upload'), { method: 'POST', body: formData });
            const uploaded = await response.json();
            if (!response.ok) throw new Error(uploaded.error || 'Upload fehlgeschlagen');
            await fetchJson('/api/music/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: uploaded.url, originalName: uploaded.originalName }),
            });
            await loadLibrary();
            if (uploadTargetPhase) {
                if (PHASES.find((p) => p.key === uploadTargetPhase))
                    handlePhaseUpdate(uploadTargetPhase, { url: uploaded.url });
                else if (EFFECTS.find((e) => e.key === uploadTargetPhase))
                    handleEffectUpdate(uploadTargetPhase, { url: uploaded.url });
            }
        } catch (e) {
            setUploadError(e.message || 'Upload fehlgeschlagen');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRemoveTrack = async (url) => {
        PHASES.forEach(({ key }) => {
            if (musicConfig[key]?.url === url) handlePhaseUpdate(key, { url: DEFAULT_MUSIC_CONFIG[key].url });
        });
        EFFECTS.forEach(({ key }) => {
            if (soundEffectsConfig[key]?.url === url) handleEffectUpdate(key, { url: DEFAULT_SOUND_EFFECTS_CONFIG[key].url });
        });
        if (bgMusic.url === url) socket.emit('bgMusic:set', { url: null, playing: false });
        if (manualAudio.url === url) socket.emit('manualAudio:set', { playing: false });
        await fetchJson(`/api/music/register?url=${encodeURIComponent(url)}`, { method: 'DELETE' });
        await loadLibrary();
    };

    // bgMusic Steuerung
    const [localBgVolume, setLocalBgVolume] = useState(bgMusic.volume);
    const [localBgFade, setLocalBgFade] = useState(bgMusic.fadeDuration ?? 1.0);
    useEffect(() => setLocalBgVolume(bgMusic.volume), [bgMusic.volume]);
    useEffect(() => setLocalBgFade(bgMusic.fadeDuration ?? 1.0), [bgMusic.fadeDuration]);
    const bgLoopEnabled = bgMusic.loop !== false;
    const activeBgTrack = allTracks.find((t) => t.url === bgMusic.url);
    const activeManualTrack = allTracks.find((t) => t.url === manualAudio.url);

    // Punktesystem – lokale Werte
    const [localScore, setLocalScore] = useState(DEFAULT_SCORE_CONFIG);
    useEffect(() => setLocalScore(scoreConfig), [scoreConfig]);

    // Zuschauer-Infotext – lokaler Entwurf
    const [localViewerInfoText, setLocalViewerInfoText] = useState(viewerInfoText);
    const [localViewerInfoLink, setLocalViewerInfoLink] = useState(viewerInfoLink);
    const [viewerInfoSaving, setViewerInfoSaving] = useState(false);
    useEffect(() => setLocalViewerInfoText(viewerInfoText), [viewerInfoText]);
    useEffect(() => setLocalViewerInfoLink(viewerInfoLink), [viewerInfoLink]);
    const handleSaveViewerInfoText = useCallback(() => {
        const trimmed = localViewerInfoText.trim();
        if (!trimmed) return;
        setViewerInfoSaving(true);
        socket.emit('viewerInfo:setText', { text: trimmed, link: localViewerInfoLink.trim() }, (res) => {
            setViewerInfoSaving(false);
            if (!res?.ok) alert(res?.error || 'Fehler beim Speichern.');
        });
    }, [localViewerInfoText, localViewerInfoLink]);

    return (
        <div className="options-view">
            <h2>Optionen</h2>

            {/* ── AUDIOEINSTELLUNGEN ─────────────────────────────── */}
            <TopSection title="Audioeinstellungen" defaultOpen={true}>

                {/* Globaler Status: was läuft gerade */}
                {(manualAudio.playing || bgMusic.playing) && (
                    <div className="audio-now-playing-bar">
                        {manualAudio.playing && manualAudio.url && (
                            <span className="audio-now-playing-item">
                                <span className="audio-now-playing-dot" />
                                <span>{activeManualTrack?.originalName || manualAudio.url}</span>
                                <button type="button" className="audio-stop-btn"
                                    onClick={() => socket.emit('manualAudio:set', { playing: false })}
                                    title="Stoppen">⏹</button>
                            </span>
                        )}
                        {bgMusic.playing && bgMusic.url && (
                            <span className="audio-now-playing-item">
                                <span className="audio-now-playing-dot audio-now-playing-dot--bg" />
                                <span>{activeBgTrack?.originalName || bgMusic.url} (Hintergrund)</span>
                                <button type="button" className="audio-stop-btn"
                                    onClick={() => socket.emit('bgMusic:set', { playing: false })}
                                    title="Stoppen">⏹</button>
                            </span>
                        )}
                    </div>
                )}

                {/* Presets */}
                <PresetsSection />

                {/* Phasenmusik */}
                <SubSection title="Phasenmusik">
                    <div className="phase-cards">
                        {PHASES.map(({ key, label, desc }) => {
                            const cfg = musicConfig[key] || DEFAULT_MUSIC_CONFIG[key];
                            const autoPlaying = soundStatus.autoPhase === key;
                            return (
                                <PhaseCard key={key} phaseKey={key} label={label} desc={desc}
                                    cfg={cfg}
                                    allTracks={allTracks} onUpdate={handlePhaseUpdate} onUpload={handleOpenUpload}
                                    onPlayToggle={handlePlayToggle}
                                    isPlaying={manualAudio.playing && manualAudio.url === cfg.url}
                                    isAutoPlaying={autoPlaying}
                                />
                            );
                        })}
                    </div>
                </SubSection>

                {/* Sound-Effekte */}
                <SubSection title="Sound-Effekte">
                    <div className="phase-cards">
                        {EFFECTS.map(({ key, label, desc }) => (
                            <EffectCard key={key} effectKey={key} label={label} desc={desc}
                                cfg={soundEffectsConfig[key] || DEFAULT_SOUND_EFFECTS_CONFIG[key]}
                                allTracks={allTracks} onUpdate={handleEffectUpdate} onUpload={handleOpenUpload}
                                onPlayToggle={handlePlayToggle}
                                isPlaying={manualAudio.playing && manualAudio.url === (soundEffectsConfig[key] || DEFAULT_SOUND_EFFECTS_CONFIG[key]).url}
                            />
                        ))}
                    </div>
                </SubSection>

                {/* Freie Hintergrundmusik */}
                <SubSection title="Freie Hintergrundmusik">
                    <div className="bg-music-controls">
                        <div className="bg-music-select-row">
                            <select value={bgMusic.url || ''} onChange={(e) => socket.emit('bgMusic:set', { url: e.target.value, playing: false })} className="phase-select">
                                <option value="">— Kein Track —</option>
                                {allTracks.map((t) => <option key={t.url} value={t.url}>{t.originalName}</option>)}
                            </select>
                            <Button variant={bgMusic.playing ? 'danger' : 'primary'} disabled={!bgMusic.url}
                                onClick={() => socket.emit('bgMusic:set', { playing: !bgMusic.playing })}>
                                {bgMusic.playing ? '⏹ Stop' : '▶ Play'}
                            </Button>
                        </div>
                        <div className="phase-row">
                            <label className="phase-field-label">Lautstärke: {Math.round(localBgVolume * 100)} %</label>
                            <input type="range" min="0" max="1" step="0.01" value={localBgVolume}
                                onChange={(e) => setLocalBgVolume(parseFloat(e.target.value))}
                                onMouseUp={() => socket.emit('bgMusic:setVolume', { volume: localBgVolume })}
                                onTouchEnd={() => socket.emit('bgMusic:setVolume', { volume: localBgVolume })}
                                className="phase-slider" />
                        </div>
                        <div className="phase-row phase-row--inline">
                            <span className="phase-field-label">Loop</span>
                            <LoopToggle active={bgLoopEnabled} onChange={(v) => socket.emit('bgMusic:set', { loop: v })} />
                        </div>
                        {bgLoopEnabled && (
                            <div className="phase-row">
                                <label className="phase-field-label">Überblendzeit: {localBgFade.toFixed(1)} s</label>
                                <input type="range" min="0.2" max="5" step="0.1" value={localBgFade}
                                    onChange={(e) => setLocalBgFade(parseFloat(e.target.value))}
                                    onMouseUp={() => socket.emit('bgMusic:set', { fadeDuration: localBgFade })}
                                    onTouchEnd={() => socket.emit('bgMusic:set', { fadeDuration: localBgFade })}
                                    className="phase-slider" />
                            </div>
                        )}
                    </div>
                </SubSection>

                {/* Audiobibliothek */}
                <SubSection title="Audiobibliothek">
                    <div className="library-upload-row">
                        <Button variant="secondary" disabled={uploading} onClick={() => handleOpenUpload(null)}>
                            {uploading ? 'Wird hochgeladen…' : '+ Neue Datei hochladen'}
                        </Button>
                        {uploadError && <span className="options-error">{uploadError}</span>}
                    </div>
                    <ul className="library-list">
                        {allTracks.map((track) => (
                            <li key={track.url} className="library-item">
                                <span className="library-name">🎵 {track.originalName}</span>
                                <Button size="sm" variant="ghost" onClick={() => handleRemoveTrack(track.url)} title="Aus Bibliothek entfernen">✕</Button>
                            </li>
                        ))}
                    </ul>
                </SubSection>

            </TopSection>

            {/* ── PUNKTESYSTEM ──────────────────────────────────── */}
            <TopSection title="Punktesystem" defaultOpen={true}>
                <SubSection title="Punkte pro Schwierigkeitsstufe">
                    <div className="score-grid">
                        {[
                            { key: 'leicht', label: 'Leicht' },
                            { key: 'mittel', label: 'Mittel' },
                            { key: 'schwer', label: 'Schwer' },
                        ].map(({ key, label }) => (
                            <div key={key} className="score-row">
                                <label className="score-label">{label}</label>
                                <div className="score-input-row">
                                    <input
                                        type="number"
                                        min="0"
                                        step="1"
                                        className="score-input"
                                        value={localScore[key] ?? DEFAULT_SCORE_CONFIG[key]}
                                        onChange={(e) => setLocalScore((p) => ({ ...p, [key]: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                                        onBlur={() => socket.emit('scoreConfig:set', { key, value: localScore[key] })}
                                        onKeyDown={(e) => e.key === 'Enter' && socket.emit('scoreConfig:set', { key, value: localScore[key] })}
                                    />
                                    <span className="score-unit">Punkte</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </SubSection>
            </TopSection>

            {/* ── ZUSCHAUER-ANSICHT ─────────────────────────────── */}
            <TopSection title="Zuschauer-Ansicht (/show ohne Login)" defaultOpen={false}>
                <SubSection title="Erklärtext">
                    <p className="options-hint">
                        Wird angezeigt, wenn jemand ohne Anmeldung auf /show geht (z.B. ein
                        Gemeinschaftsbildschirm). Daneben erscheinen automatisch die Adresse
                        dieser Seite und ein QR-Code dazu.
                    </p>
                    <textarea
                        className="viewer-info-textarea"
                        rows={3}
                        value={localViewerInfoText}
                        onChange={(e) => setLocalViewerInfoText(e.target.value)}
                    />
                    <label className="options-hint" htmlFor="viewer-info-link">
                        Link (für Text + QR-Code) — leer lassen für die automatische Adresse
                        dieser Seite ({typeof window !== 'undefined' ? window.location.origin : ''})
                    </label>
                    <input
                        id="viewer-info-link"
                        type="url"
                        className="viewer-info-textarea"
                        placeholder={typeof window !== 'undefined' ? window.location.origin : 'https://…'}
                        value={localViewerInfoLink}
                        onChange={(e) => setLocalViewerInfoLink(e.target.value)}
                    />
                    <Button
                        variant="primary"
                        size="sm"
                        disabled={viewerInfoSaving || !localViewerInfoText.trim()}
                        onClick={handleSaveViewerInfoText}
                    >
                        {viewerInfoSaving ? 'Speichert…' : 'Speichern'}
                    </Button>
                </SubSection>
            </TopSection>

            <input ref={fileInputRef} type="file" accept="audio/*" style={{ display: 'none' }}
                onChange={(e) => handleFileUpload(e.target.files?.[0])} />
        </div>
    );
}

export default OptionsView;
